import { TokenResult } from "@stripe/stripe-js";
import { flash } from "@xola/ui-kit";
import { getPaymentLineItems, handleSuccessfulSubmission, isDateBeforeNow, isExperienceProduct } from "common";
import { FormikHelpers } from "formik";
import { produce } from "immer";
import { isString } from "lodash";
import React, { useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
    Experience,
    LineItemType,
    Package,
    Payment,
    PaymentLineItemStatus,
    PaymentMethod,
    Purchase,
    PurchaseStatus,
} from "types";
import { object } from "yup";
import { api } from "../../api/api";
import { getAuthNetPurchasePayload, handlePaymentError } from "../../helpers/authNetPaymentHelper";
import { ScrollToFieldError } from "../../helpers/validationHelper";
import { useSeller } from "../../hooks/useAccount";
import { useInvalidate } from "../../hooks/useInvalidation";
import { useVisibility } from "../../hooks/useVisibility";
import { hasCreditCardLineItem, isReservePurchaseProcess } from "../../models/CreatePurchaseState";
import { isSharedDemographicsPackage } from "../../models/Package";
import { getCreditCardLineItem, isHold, needsPrepare, shouldEnableDrawerEject } from "../../models/Purchase";
import { invalidateSellerBalance, isAuthorizeNetEnabled } from "../../models/Seller";
import { getAutoPrint } from "../../storages/PrinterStorage";
import {
    clearDraftPurchasesCache,
    CreatePurchaseState,
    useCreatePurchaseStore,
} from "../../stores/createPurchaseStore";
import { updateCache } from "../../utils/updateCache";
import { Printer } from "../Common/Printer";
import { XolaFormik } from "../Common/XolaFormik";
import { invalidateArrivalCache } from "../DatePicker/useArrivalTimeslots";
import { PaymentInputRef, TokenizePaymentParams } from "../Input/PaymentInputRef";
import { ContentLayout } from "../Layout/ContentLayout";
import { PurchaseReceipt } from "../Receipt/PurchaseReceipt";
import {
    CheckInAndPrintTicket,
    getGuestsForPrintTicketModal,
} from "../Settings/App/PurchaseCreate/CheckInAndPrintTicket";
import { CheckoutGuestQuestionnaire } from "./CheckoutGuestQuestionnaire";
import { CheckoutPurchaseProductFields } from "./CheckoutPurchaseProductFields";
import { CheckoutFormValues } from "./CheckoutPurchaseScreen.types";
import { CheckoutPurchaseScreenFooter } from "./CheckoutPurchaseScreenFooter";
import { CreatePurchaseCustomerFields } from "./CreatePurchaseCustomerFields";
import { CreatePurchaseInternalNoteFields } from "./CreatePurchaseInternalNoteFields";
import { CreatePurchasePaymentDetails } from "./CreatePurchasePaymentDetails";
import { CreatePurchaseSidebar } from "./CreatePurchaseSidebar";
import {
    getDetailsHeading,
    getPayload,
    getPostCreateBookingAction,
    hasCouponApplied,
    hasZeroDiscountCoupon,
    isPurchaseNotFoundError,
    resetPurchase,
} from "./helpers/PurchaseCreateHelpers";
import { useCheckoutPurchaseInitialValues } from "./hooks/useCheckoutPurchaseInitialValues";
import { useCheckoutPurchaseScreenValidations } from "./hooks/useCheckoutPurchaseScreenValidations";
import { useSetCustomerNameRequired } from "./hooks/useSetCustomerNameRequired";
import { useUpsertPurchase } from "./hooks/useUpsertPurchase";
import { PastBookingAlert } from "./PastBookingAlert";
import { RemoveCouponConfirmModal } from "./RemoveCouponConfirmModal";
import { useCustomerDetailsStore } from "./store/customerDetailsStore";
import { usePurchaseResponseStore } from "./store/usePurchaseResponseStore";
import { BillingInfoError } from "@/errors/requireBillingInfoError";
import { isCreditCardRequired, showCustomerFields } from "@/models/Experience";
import { usePurchaseSearchStore } from "@/stores/purchaseSearchStore";

interface CheckoutPurchaseScreenProps {
    product?: Package;
    onBack: () => void;
}

export const CheckoutPurchaseScreen = ({ product: packageProduct, onBack }: CheckoutPurchaseScreenProps) => {
    const seller = useSeller();
    const history = useHistory();
    const invalidate = useInvalidate();
    const paymentInputRef = useRef<PaymentInputRef>(null);

    const { resetState, setIsEMV } = useCreatePurchaseStore.getState().actions;

    const isOneStepCheckout = useCreatePurchaseStore((state) => state.isOneStepCheckout());
    const itemProduct = useCreatePurchaseStore((state) => state.getActiveProduct<Experience>());
    const product = packageProduct ?? itemProduct;
    const [purchaseToPrint, setPurchaseToPrint] = useState<Purchase>();
    const [enableDrawerEject, setEnableDrawerEject] = useState<boolean>(false);
    const [purchaseForPrintModal, setPurchaseForPrintModal] = useState<Purchase>();
    const [isRemoveCouponConfirmModalOpen, setIsRemoveCouponConfirmModalOpen] = useState(false);
    const showCustomerFieldsPreference = useCustomerDetailsStore((state) => state.showCustomerFields);
    const showCustomerForm = useCreatePurchaseStore((state) => !state.isWaitListMode());

    const validationSchema = useCheckoutPurchaseScreenValidations();

    const [isSuccess, setIsSuccess] = useState(false);

    const [isPrintTicketModalOpen, { show: showPrintTicketModal, hide: hidePrintTicketModal }] = useVisibility(false);

    const isSellerCheckInPreferenceEnabled = seller.preferences?.checkIn?.enabled;

    useSetCustomerNameRequired(product);

    const { mutateAsync: upsertPurchase, isLoading } = useUpsertPurchase();

    const initialValues = useCheckoutPurchaseInitialValues();

    const postValidate = (actions: FormikHelpers<CheckoutFormValues>) => {
        const { payment } = useCreatePurchaseStore.getState().paymentLineItem;
        const isDeposit = useCreatePurchaseStore.getState().hasDeposit();
        const earliestArrivalDateTime = useCreatePurchaseStore.getState().getEarliestArrivalDateTime();
        const { balanceDueOffset } = useCreatePurchaseStore.getState().getActiveItem();
        const { paymentDueOffset } = useCreatePurchaseStore.getState().getActiveItem();
        if (isDeposit && isDateBeforeNow(earliestArrivalDateTime, balanceDueOffset ?? 0)) {
            actions.setFieldError("depositDuration", " date cannot be in the past");
            return true;
        }

        const isInvoice = payment.method === PaymentMethod.INVOICE;

        if (isInvoice && isDateBeforeNow(earliestArrivalDateTime, paymentDueOffset ?? 0)) {
            actions.setFieldError("arrivalOffset", " date cannot be in the past");
            return true;
        }

        return false;
    };

    const getPayloadForPaymentGateway = async (payload: CreatePurchaseState) => {
        const { paymentLineItem, cardInputMethod } = useCreatePurchaseStore.getState();
        const { dueNow, amount } = usePurchaseResponseStore.getState().purchase;
        const paymentStatus = paymentLineItem.status;
        const shouldCollectCC = amount === 0 && isExperienceProduct(product) && isCreditCardRequired(product);

        if (isAuthorizeNetEnabled(seller) && hasCreditCardLineItem(payload)) {
            if (isString(paymentLineItem.payment.card)) {
                return payload;
            }

            const response = (await paymentInputRef.current?.tokenize()) as TokenizePaymentParams;
            if (!response) {
                return;
            }

            return getAuthNetPurchasePayload(response, payload, paymentStatus, cardInputMethod);
        }

        // Payload for Stripe seller
        const shouldTokenize = paymentStatus === PaymentLineItemStatus.PENDING || dueNow === 0;

        const tokenResult: TokenResult | null = shouldTokenize
            ? ((await paymentInputRef.current?.tokenize()) as TokenResult | null) ?? null
            : null;

        return produce(payload, (draft) => {
            let paymentLine = draft.lineItems.find((lineItem) => {
                return (
                    "status" in lineItem &&
                    lineItem.type === LineItemType.PAYMENT &&
                    lineItem.status === PaymentLineItemStatus.PENDING &&
                    lineItem.payment?.method === PaymentMethod.CREDIT_CARD
                );
            });

            if (!paymentLine && shouldCollectCC) {
                paymentLine = { ...paymentLineItem };
                draft.lineItems.push(paymentLine);
            }

            if (paymentLine && tokenResult?.token?.id) {
                paymentLine.payment = {
                    token: { id: tokenResult?.token?.id },
                    method: PaymentMethod.CREDIT_CARD,
                };
            }
        });
    };

    const handleSubmit = async (values: CheckoutFormValues, actions: FormikHelpers<CheckoutFormValues>) => {
        const payload = getPayload(values) as CreatePurchaseState;
        const { paymentLineItem, balance, dueNow = 0 } = useCreatePurchaseStore.getState();

        if (hasZeroDiscountCoupon(payload.items)) {
            setIsRemoveCouponConfirmModalOpen(true);
            return;
        }

        if (postValidate(actions)) {
            return;
        }

        useCreatePurchaseStore.setState({ isSubmitting: true });

        let purchasePayload = await getPayloadForPaymentGateway(payload);
        if (!purchasePayload) {
            useCreatePurchaseStore.setState({ isSubmitting: false });
            return;
        }

        const isPayLater = paymentLineItem.payment.method === PaymentMethod.LATER;

        // Added condition to get balance due equal to 0 when using coupon with 100% discount, and deposit field has value more than 0
        const paymentInputParams = { amountDue: balance === 0 ? 0 : dueNow };
        const paymentMethod = paymentLineItem.payment.method;
        const isCashPayment = paymentMethod === PaymentMethod.CASH;
        const isReservePurchase = isReservePurchaseProcess(paymentMethod, dueNow, paymentLineItem.status);
        const shouldPrepare = (!isPayLater && paymentInputRef.current && needsPrepare()) || isCashPayment;

        if (shouldPrepare && !isReservePurchase) {
            const response = await paymentInputRef.current?.prepare?.(paymentInputParams);
            if (!response) {
                useCreatePurchaseStore.setState({ isSubmitting: false });
                return;
            }

            if (isCashPayment) {
                purchasePayload = produce(purchasePayload, (draft) => {
                    const paymentLineItem = draft.lineItems.find(
                        (lineItem) =>
                            lineItem.type === LineItemType.PAYMENT && lineItem.payment?.method === PaymentMethod.CASH,
                    );
                    if (paymentLineItem) {
                        paymentLineItem.payment = response as Payment;
                    }
                });
            }
        }

        try {
            const createdPurchase = await upsertPurchase(purchasePayload);

            const creditCardPaymentLineItem = getCreditCardLineItem(createdPurchase);
            const clientSecret = creditCardPaymentLineItem?.payment?.paymentIntent?.clientSecret;
            if (clientSecret) {
                try {
                    await paymentInputRef.current?.confirm({ clientSecret });
                } catch (error) {
                    if (isHold(createdPurchase)) {
                        // Release the order if the payment failed otherwise the availability will be held up
                        if (!(error instanceof BillingInfoError)) {
                            await api.delete(`/purchases/${createdPurchase.id}`);
                        }
                    } else {
                        throw error;
                    }

                    return;
                }
            }

            if (isHold(createdPurchase) && !isAuthorizeNetEnabled(seller)) {
                const { data: updatedPurchase } = await api.patch<Purchase>(`/purchases/${createdPurchase.id}`, {
                    status: PurchaseStatus.COMMITTED,
                    lineItems: creditCardPaymentLineItem ? [{ id: creditCardPaymentLineItem.id }] : [],
                });
                const { isEMV } = useCreatePurchaseStore.getState();
                if (isEMV) {
                    setIsEMV(false);
                }

                handlePaymentSuccess(updatedPurchase);
            } else {
                handlePaymentSuccess(createdPurchase);
            }

            if (hasCouponApplied(payload.items)) {
                invalidate("codes");
            }

            if ((createdPurchase.status = PurchaseStatus.COMMITTED)) {
                setIsSuccess(true);
                useCreatePurchaseStore.getState().actions.setPurchase((draft) => {
                    draft.status = PurchaseStatus.COMMITTED;
                });
                // Removing the search query as we are redirecting to the purchase page as we need show the latest purchase
                usePurchaseSearchStore.getState().setSearchQuery("");

                // Clear draft purchases cache since we don't need after completion.
                clearDraftPurchasesCache();
            }

            // Invalidate the arrival cache that we have for this product
            for (const item of createdPurchase.items ?? []) {
                invalidateArrivalCache(item.product.id);
            }
        } catch (error: any) {
            // Handles the rare case when the seller was afk for long enough on this screen for the purchase draft to be deleted, refer to the X2-9369.
            if (isPurchaseNotFoundError(error)) {
                resetPurchase();
            }

            // This is required to open additional billing details modal in case there is an error while making authorize.net payment
            handlePaymentError(error, seller, paymentInputRef);
        } finally {
            useCreatePurchaseStore.setState({ isSubmitting: false });
        }
    };

    const handleCancel = () => {
        resetState();
        history.push("/cart/init");
    };

    const handlePaymentSuccess = (createdPurchase: Purchase) => {
        // Do these actions only if the purchase is not partner experience purchase.
        // A dummy purchase (partner experience on source seller) has no items.
        const isDummyPartnerPurchase = !createdPurchase.items?.length;
        if (createdPurchase.seller.id === seller.id && !isDummyPartnerPurchase) {
            updateCache("purchases", createdPurchase);
            updateCache("purchaseItems", createdPurchase.items);
            invalidate("timeslots");
            const { paymentLineItem } = useCreatePurchaseStore.getState();
            if (paymentLineItem.payment.method === PaymentMethod.CREDIT_CARD) {
                invalidateSellerBalance();
            }

            if ((createdPurchase.status = PurchaseStatus.COMMITTED)) {
                setIsSuccess(true);
            }

            if (useCreatePurchaseStore.getState().purchase.splitPayment.enabled) {
                routeToPurchases(createdPurchase);
                return;
            }
        } else {
            // Just invalidate purchases when partner experience purchase is done, because of the put/patch error
            invalidate("purchases");
        }

        const drawerEjectPreference = shouldEnableDrawerEject(createdPurchase);
        const paymentLineItems = getPaymentLineItems(createdPurchase);
        const enablePrintActions = drawerEjectPreference || paymentLineItems.length > 0;
        if (getAutoPrint() && enablePrintActions) {
            setEnableDrawerEject(drawerEjectPreference);
            // need to print receipt only for a purchase with at least one completed payment.
            // refer X2-7517
            if (paymentLineItems.length > 0) {
                setPurchaseToPrint(createdPurchase);
            }
        } else {
            handlePrintFinish(createdPurchase);
        }
    };

    const handlePrintFinish = (purchase = purchaseToPrint) => {
        if (purchase && (purchase.seller.id !== seller.id || !purchase.items?.length)) {
            // This is a partner purchase or dummy partner purchase, just route to purchases
            routeToPurchases(purchase);
            return;
        }

        const guests = getGuestsForPrintTicketModal(purchase, isSellerCheckInPreferenceEnabled);
        if (seller.preferences.ticket?.autoPrint && guests.length > 0 && purchase && purchase.seller.id === seller.id) {
            setPurchaseForPrintModal(purchase);
            showPrintTicketModal();
        } else {
            routeToPurchases(purchase);
        }
    };

    const routeToPurchases = async (purchase = purchaseToPrint) => {
        if (!purchase) {
            return;
        }

        let uri = getPostCreateBookingAction(seller, purchase);
        if (purchase.splitPayment.enabled) {
            uri += "/?showSplitPay=true";
        }

        handleSuccessfulSubmission(() => {
            history.push(uri);
            resetState();
        });
    };

    // Authorize.net payment with additional billing
    const handleAdditionalBillingSubmit = async (
        tokenizePaymentParams: TokenizePaymentParams,
        values: CheckoutFormValues,
    ) => {
        const payload = getPayload(values) as CreatePurchaseState;
        const paymentStatus = useCreatePurchaseStore.getState().paymentLineItem.status;
        const purchasePayload = getAuthNetPurchasePayload(tokenizePaymentParams, payload, paymentStatus);
        let createdPurchase = null;

        try {
            createdPurchase = await upsertPurchase(purchasePayload);
            handlePaymentSuccess(createdPurchase);
        } catch (error: any) {
            paymentInputRef.current?.onPaymentError?.(error.response?.data?.global);
        } finally {
            useCreatePurchaseStore.setState({ isSubmitting: false });
        }
    };

    const handleAdditionalBillingSubmitSuccess = async () => {
        const { purchaseId } = useCreatePurchaseStore.getState();
        try {
            if (purchaseId) {
                useCreatePurchaseStore.setState({ isSubmitting: true });
                const createdPurchase = await api.patch<Purchase>(`/purchases/${purchaseId}`, {
                    status: PurchaseStatus.COMMITTED,
                });
                handlePaymentSuccess(createdPurchase.data);
            }
        } catch (error: any) {
            flash.danger(error?.message || "An error occurred while processing the payment.");
        } finally {
            useCreatePurchaseStore.setState({ isSubmitting: false });
        }
    };

    const isSharedDemographicPackage = isSharedDemographicsPackage(product);

    const gridStyling = isSharedDemographicPackage ? "col-span-9 mt-4 space-y-8" : "col-span-12";

    return (
        <div className="grid h-full grid-cols-12">
            <ContentLayout isWide classNames={{ root: "relative col-span-6 h-full 2xl:col-span-7" }}>
                <div className="space-y-7">
                    {!(isExperienceProduct(product) && !showCustomerFields(product)) && (
                        <h3>{getDetailsHeading(product.object)} Details</h3>
                    )}

                    <CheckoutPurchaseProductFields product={product} />

                    <XolaFormik<CheckoutFormValues>
                        enableReinitialize
                        enablePrompt={false}
                        initialValues={initialValues}
                        validationSchema={object().shape(validationSchema)}
                        onSubmit={handleSubmit}
                    >
                        <div className="space-y-4">
                            {showCustomerForm && (
                                <>
                                    {isOneStepCheckout && <hr className="!my-8 text-gray-light" />}

                                    <div className="grid grid-cols-12">
                                        <div className={gridStyling}>
                                            {showCustomerFieldsPreference && (
                                                <CreatePurchaseCustomerFields product={product} />
                                            )}

                                            <CheckoutGuestQuestionnaire product={product} />

                                            <CreatePurchasePaymentDetails
                                                ref={paymentInputRef}
                                                validate={() => paymentInputRef.current?.getError?.()}
                                                onAdditionalDetailsSubmit={handleAdditionalBillingSubmit}
                                                onAdditionalDetailsSubmitSuccess={handleAdditionalBillingSubmitSuccess}
                                            />
                                        </div>
                                    </div>

                                    <hr className="!mt-4 !mb-8 text-gray-light" />

                                    <CreatePurchaseInternalNoteFields />
                                </>
                            )}

                            <PastBookingAlert />

                            <CheckoutPurchaseScreenFooter
                                isLoading={isLoading}
                                secondaryText={isOneStepCheckout ? "Cancel" : "Back"}
                                isSuccess={isSuccess}
                                onCancel={isOneStepCheckout ? handleCancel : onBack}
                            />

                            <ScrollToFieldError />
                        </div>
                    </XolaFormik>
                </div>
            </ContentLayout>
            <div className="col-span-6 shrink-0 space-y-6 bg-gray-lighter 2xl:col-span-5">
                <CreatePurchaseSidebar />
            </div>
            {purchaseToPrint && (
                <Printer shouldStart enablePaperCut enableDrawerEject={enableDrawerEject} onFinish={handlePrintFinish}>
                    <PurchaseReceipt purchase={purchaseToPrint} />
                </Printer>
            )}
            {purchaseForPrintModal && isPrintTicketModalOpen && (
                <CheckInAndPrintTicket
                    isOpen={isPrintTicketModalOpen}
                    purchase={purchaseForPrintModal}
                    guests={getGuestsForPrintTicketModal(purchaseForPrintModal, isSellerCheckInPreferenceEnabled)}
                    onComplete={() => {
                        hidePrintTicketModal();
                        routeToPurchases(purchaseForPrintModal);
                    }}
                />
            )}
            {isRemoveCouponConfirmModalOpen && (
                <RemoveCouponConfirmModal
                    isOpen={isRemoveCouponConfirmModalOpen}
                    onClose={() => setIsRemoveCouponConfirmModalOpen(false)}
                />
            )}
        </div>
    );
};
